import React, { useEffect, useReducer, useState } from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'

import { ToastType, useToast } from '../../../hooks/useToast'
import { useChangeBrandForPartsMutation } from '../../../modules/store/datahubApi/datahubApi'
import { partReducer } from '../../../pages/NewPart/partReducer'
import { Part, partFields, partInput, PartListSearchParams } from '../../../types/part/partTypes'
import Loader from '../../_atoms/Loader/Loader'
import OverlayWithText from '../../_atoms/OverlayWithText/OverlayWithText'
import PartCreateUpdateTable from '../PartCreateUpdateTable/PartCreateUpdateTable'

import { initialPartState } from './initialPartState'
import styles from './styles.module.scss'

interface Props {
    isOpen: boolean
    closeModal: () => void
    parts: Part[]
    confirmChanges: () => void
    partListSearchParams?: PartListSearchParams
}

export const ModalChangeBrand = ({ isOpen, closeModal, parts, confirmChanges, partListSearchParams }: Props) => {
    const [state, dispatch] = useReducer(partReducer, initialPartState)
    const [fetchChangeBrand, { isLoading, error, data }] = useChangeBrandForPartsMutation()
    const [noChanges, setNoChanges] = useState(false)
    const [onlyDedicatedCatalogs, setOnlyDedicatedCatalogs] = useState(false)

    const onCloseModal = () => {
        closeModal()
    }

    const getField = (fieldToSearch: partFields) =>
        state.fields.find((field: partInput) => field.field === fieldToSearch)

    const brand = String(getField(partFields.PartBrand)?.value || '').trim()

    const confirm = async () => {
        if (!brand) {
            return
        }

        setNoChanges(false)
        setOnlyDedicatedCatalogs(false)
        fetchChangeBrand({
            partsId: parts.map(part => part.id),
            brand,
            params: partListSearchParams,
        })
    }

    useEffect(() => {
        if (!data) {
            return
        }

        if (data.updatedCount === 0) {
            if ((data.skippedCount ?? 0) > 0) {
                setOnlyDedicatedCatalogs(true)
                return
            }
            setNoChanges(true)
            return
        }

        confirmChanges()
        closeModal()
    }, [closeModal, confirmChanges, data])

    useToast({ isTriggered: !!error, type: ToastType.ERROR, message: 'Ошибка изменения бренда' })
    useToast({
        isTriggered: !!data && data.updatedCount > 0,
        type: ToastType.SUCCESS,
        message:
            (data?.skippedCount ?? 0) > 0
                ? `Бренд изменён (${data?.updatedCount ?? 0} запч.). Пропущено ${data?.skippedCount}: у них свой справочник бренда`
                : `Бренд успешно изменён (${data?.updatedCount ?? 0} запч.)`,
    })
    useToast({
        isTriggered: noChanges,
        type: ToastType.ERROR,
        message: 'Нет изменений — бренд уже установлен',
    })
    useToast({
        isTriggered: onlyDedicatedCatalogs,
        type: ToastType.ERROR,
        message: 'У этих запчастей свой справочник бренда: масла, магнитолы, шины, акустика, усилители, чехлы',
    })

    return (
        <Modal show={isOpen} onHide={onCloseModal} centered dialogClassName={styles.modal}>
            {isLoading && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={100} thicknessInPx={10} />
                </OverlayWithText>
            )}
            <Modal.Header closeButton>
                <Modal.Title>
                    <b>Изменить бренд</b>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning" className={styles.disclaimer}>
                    Изменится только бренд запчасти. Масла, магнитолы, шины, акустика, усилители и чехлы не затрагиваются — у них свои справочники бренда.
                </Alert>
                <PartCreateUpdateTable dispatch={dispatch} fields={state.fields} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCloseModal}>
                    Отмена
                </Button>
                <Button variant="primary" onClick={confirm} disabled={!brand || isLoading}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
